import React, { useState, useRef, useEffect, Suspense, useMemo } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MOVEMENTS, buildMovementHierarchy } from "../data/MovementsData";
import FiveOhSevenModel from "../components/FiveOhSevenModel";

// ── CONSTANTS & DEFAULTS — matched exactly to Portfolio.js ───────────────────
const DEBUG_LIGHTING = false;

const DEFAULT_LIGHTS = {
    ambientIntensity: 0.55,
    keyIntensity: 0.35,
    keyX: -20, keyY: -1.0, keyZ: 4,
    fillIntensity: 0.3,
    fillX: 15.5, fillY: -0.5, fillZ: -0.5,
    rimIntensity: 0.8,
    rimX: 1.0, rimY: 0.5, rimZ: 5.0,
    metalness: 0.95,
    roughness: 0.55,
    ropeMetalness: 0.55,
    ropeRoughness: 0.9,
};

function modelDebugDefaults(movement) {
    return {
        rotX: movement.rotation?.[0] ?? 0,
        rotY: movement.rotation?.[1] ?? 0,
        rotZ: movement.rotation?.[2] ?? 0,
        rotationAxis: movement.rotationAxis ?? "y",
        viewX: movement.initialView?.[0] ?? 0,
        viewY: movement.initialView?.[1] ?? 0,
        viewZ: movement.initialView?.[2] ?? 10,
        initialZoom: movement.initialZoom ?? 100,
    };
}

// ── Derive numeric ID from a movement entry ───────────────────────────────────
function movementNumber(m) {
    return m.number ?? parseInt(m.id.replace(/\D/g, ""), 10);
}

// ── DEBUG PANEL — identical structure to Portfolio.js, with Material tab ─────
function Slider({ label, value, min, max, step = 0.05, onChange }) {
    return (
        <div style={{ marginBottom: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px", gap: "6px" }}>
                <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", fontFamily: "monospace", flexShrink: 0 }}>{label}</span>
                <input
                    type="number" min={min} max={max} step={step}
                    value={Number(value).toFixed(step < 1 ? 2 : 0)}
                    onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v))); }}
                    style={{ width: "62px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "3px", color: "rgba(255,255,255,0.85)", fontSize: "10px", fontFamily: "monospace", padding: "1px 4px", textAlign: "right", flexShrink: 0 }}
                />
            </div>
            <input type="range" min={min} max={max} step={step} value={value}
                onChange={e => onChange(parseFloat(e.target.value))}
                style={{ width: "100%", accentColor: "#aaa", cursor: "pointer" }} />
        </div>
    );
}

function AxisSelect({ value, onChange }) {
    return (
        <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
            {["x", "y", "z"].map(ax => (
                <button key={ax} onClick={() => onChange(ax)} style={{
                    flex: 1, padding: "4px",
                    background: value === ax ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.05)",
                    border: value === ax ? "1px solid rgba(255,255,255,0.4)" : "1px solid rgba(255,255,255,0.1)",
                    color: value === ax ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.4)",
                    borderRadius: "3px", cursor: "pointer", fontSize: "11px", fontFamily: "monospace",
                }}>{ax}</button>
            ))}
        </div>
    );
}

function LightDebugPanel({ lights, onChange, paused, onTogglePause, onReset, modelDebug, onModelDebugChange, activeMovement }) {
    const [open, setOpen] = useState(true);
    const [tab, setTab] = useState("model");
    const set = (key) => (val) => onChange({ ...lights, [key]: val });
    const setMd = (key) => (val) => onModelDebugChange({ ...modelDebug, [key]: val });

    const piStr = (v) => {
        const frac = v / Math.PI;
        if (Math.abs(frac) < 0.001) return "0";
        if (Math.abs(Math.abs(frac) - 0.5) < 0.001) return frac > 0 ? "Math.PI / 2" : "-Math.PI / 2";
        if (Math.abs(Math.abs(frac) - 1) < 0.001) return frac > 0 ? "Math.PI" : "-Math.PI";
        if (Math.abs(Math.abs(frac) - 0.25) < 0.001) return frac > 0 ? "Math.PI / 4" : "-Math.PI / 4";
        return v.toFixed(3);
    };

    const printMovementBlock = () => {
        const md = modelDebug; const l = lights;
        const block = `    {
        id: "${activeMovement.id}",
        name: "${activeMovement.name}",
        number: ${activeMovement.number},
        category: "${activeMovement.category}",
        group: "${activeMovement.group}",
        description: "${activeMovement.description}",
        urls: ${JSON.stringify(activeMovement.urls ?? [activeMovement.url])},
        rotation: [${piStr(md.rotX)}, ${piStr(md.rotY)}, ${piStr(md.rotZ)}],
        rotationAxis: "${md.rotationAxis}",
        initialView: [${md.viewX.toFixed(1)}, ${md.viewY.toFixed(1)}, ${md.viewZ.toFixed(1)}],
        initialZoom: ${md.initialZoom},
        // ambient: ${l.ambientIntensity.toFixed(2)}, key: ${l.keyIntensity.toFixed(2)} @ [${l.keyX.toFixed(1)},${l.keyY.toFixed(1)},${l.keyZ.toFixed(1)}]
        // fill: ${l.fillIntensity.toFixed(2)} @ [${l.fillX.toFixed(1)},${l.fillY.toFixed(1)},${l.fillZ.toFixed(1)}]
        // rim: ${l.rimIntensity.toFixed(2)} @ [${l.rimX.toFixed(1)},${l.rimY.toFixed(1)},${l.rimZ.toFixed(1)}]
        // metalness: ${l.metalness.toFixed(2)}, roughness: ${l.roughness.toFixed(2)}
    },`;
        console.log(block);
        alert("Movement block printed to console (F12) — paste into MovementsData.js");
    };

    const tabStyle = (t) => ({
        flex: 1, padding: "5px", cursor: "pointer", fontSize: "9px",
        letterSpacing: "0.1em", textTransform: "uppercase", border: "none",
        background: tab === t ? "rgba(255,255,255,0.1)" : "transparent",
        color: tab === t ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)",
        borderBottom: tab === t ? "1px solid rgba(255,255,255,0.4)" : "1px solid rgba(255,255,255,0.08)",
    });

    return (
        <div style={{
            position: "fixed", bottom: "50px", right: 0, width: "260px",
            background: "rgba(0,0,0,0.92)", border: "1px solid rgba(255,255,255,0.12)",
            borderRight: "none", borderRadius: "8px 0 0 8px", zIndex: 9999,
            fontFamily: "monospace", overflow: "hidden",
        }}>
            <div onClick={() => setOpen(o => !o)} style={{
                padding: "8px 12px", borderBottom: open ? "1px solid rgba(255,255,255,0.08)" : "none",
                cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
            }}>
                <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.7)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Debug</span>
                <button onClick={e => { e.stopPropagation(); onTogglePause(); }} style={{
                    background: paused ? "rgba(255,200,80,0.15)" : "rgba(255,255,255,0.08)",
                    border: paused ? "1px solid rgba(255,200,80,0.4)" : "1px solid rgba(255,255,255,0.15)",
                    color: paused ? "rgba(255,200,80,0.9)" : "rgba(255,255,255,0.6)",
                    fontSize: "10px", padding: "2px 8px", borderRadius: "3px", cursor: "pointer", letterSpacing: "0.08em", whiteSpace: "nowrap",
                }}>{paused ? "▶ play" : "⏸ pause"}</button>
                <button onClick={e => { e.stopPropagation(); onReset(); }} style={{
                    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)",
                    color: "rgba(255,255,255,0.45)", fontSize: "10px", padding: "2px 8px",
                    borderRadius: "3px", cursor: "pointer", letterSpacing: "0.08em", whiteSpace: "nowrap",
                }}>↺ reset</button>
                <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)", marginLeft: "auto" }}>{open ? "▼" : "▲"}</span>
            </div>
            {open && (
                <>
                    <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                        <button style={tabStyle("model")} onClick={() => setTab("model")}>Model</button>
                        <button style={tabStyle("lights")} onClick={() => setTab("lights")}>Lights</button>
                        <button style={tabStyle("material")} onClick={() => setTab("material")}>Material</button>
                    </div>
                    <div style={{ padding: "10px 12px", maxHeight: "60vh", overflowY: "auto" }}>
                        {tab === "model" && (<>
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>Initial rotation</div>
                            <Slider label="x" value={modelDebug.rotX} min={-Math.PI} max={Math.PI} step={0.01} onChange={setMd("rotX")} />
                            <Slider label="y" value={modelDebug.rotY} min={-Math.PI} max={Math.PI} step={0.01} onChange={setMd("rotY")} />
                            <Slider label="z" value={modelDebug.rotZ} min={-Math.PI} max={Math.PI} step={0.01} onChange={setMd("rotZ")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Auto-rotate axis</div>
                            <AxisSelect value={modelDebug.rotationAxis} onChange={(v) => onModelDebugChange({ ...modelDebug, rotationAxis: v })} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Camera position</div>
                            <Slider label="x" value={modelDebug.viewX} min={-20} max={20} step={0.5} onChange={setMd("viewX")} />
                            <Slider label="y" value={modelDebug.viewY} min={-20} max={20} step={0.5} onChange={setMd("viewY")} />
                            <Slider label="z" value={modelDebug.viewZ} min={1} max={30} step={0.5} onChange={setMd("viewZ")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Initial zoom %</div>
                            <Slider label="zoom" value={modelDebug.initialZoom} min={20} max={300} step={1} onChange={setMd("initialZoom")} />
                            <button onClick={printMovementBlock} style={{
                                width: "100%", marginTop: "12px",
                                background: "rgba(100,200,150,0.1)", border: "1px solid rgba(100,200,150,0.3)",
                                color: "rgba(100,200,150,0.9)", padding: "6px", cursor: "pointer",
                                borderRadius: "4px", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase",
                            }}>Copy block to console</button>
                        </>)}
                        {tab === "lights" && (<>
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>Ambient</div>
                            <Slider label="intensity" value={lights.ambientIntensity} min={0} max={2} onChange={set("ambientIntensity")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Key light</div>
                            <Slider label="intensity" value={lights.keyIntensity} min={0} max={3} onChange={set("keyIntensity")} />
                            <Slider label="x" value={lights.keyX} min={-20} max={20} step={0.5} onChange={set("keyX")} />
                            <Slider label="y" value={lights.keyY} min={-20} max={20} step={0.5} onChange={set("keyY")} />
                            <Slider label="z" value={lights.keyZ} min={-20} max={20} step={0.5} onChange={set("keyZ")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Fill light</div>
                            <Slider label="intensity" value={lights.fillIntensity} min={0} max={2} onChange={set("fillIntensity")} />
                            <Slider label="x" value={lights.fillX} min={-20} max={20} step={0.5} onChange={set("fillX")} />
                            <Slider label="y" value={lights.fillY} min={-20} max={20} step={0.5} onChange={set("fillY")} />
                            <Slider label="z" value={lights.fillZ} min={-20} max={20} step={0.5} onChange={set("fillZ")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Rim light</div>
                            <Slider label="intensity" value={lights.rimIntensity} min={0} max={2} onChange={set("rimIntensity")} />
                            <Slider label="x" value={lights.rimX} min={-20} max={20} step={0.5} onChange={set("rimX")} />
                            <Slider label="y" value={lights.rimY} min={-20} max={20} step={0.5} onChange={set("rimY")} />
                            <Slider label="z" value={lights.rimZ} min={-20} max={20} step={0.5} onChange={set("rimZ")} />
                        </>)}
                        {tab === "material" && (<>
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>PBR Material (GLTF overrides)</div>
                            <Slider label="metalness" value={lights.metalness} min={0} max={1} onChange={set("metalness")} />
                            <Slider label="roughness" value={lights.roughness} min={0} max={1} onChange={set("roughness")} />
                            <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.3)", letterSpacing: "0.12em", textTransform: "uppercase", margin: "10px 0 6px" }}>Rope Material</div>
                            <Slider label="metalness" value={lights.ropeMetalness} min={0} max={1} onChange={set("ropeMetalness")} />
                            <Slider label="roughness" value={lights.ropeRoughness} min={0} max={1} onChange={set("ropeRoughness")} />
                        </>)}
                    </div>
                </>
            )}
        </div>
    );
}

// ── MOVEMENT TAB BAR — mirrors Portfolio.js HierarchyTabBar exactly ──────────
function MovementTabBar({ active, onSelect, navState, onNavState, hierarchy, showDesc, setShowDesc }) {
    const scrollRef = useRef(null);
    const isMobile = window.innerWidth < 640;

    const btnStyle = (isActive, isAccent = false) => ({
        border: "none", background: "none", cursor: "pointer", padding: "6px 12px",
        color: isActive ? "rgba(255,255,255,0.9)" : isAccent ? "rgba(255,200,80,0.5)" : "rgba(255,255,255,0.3)",
        fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase",
        fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
    });

    useEffect(() => {
        if (scrollRef.current) {
            const activeBtn = scrollRef.current.querySelector('[data-active="true"]');
            if (activeBtn) activeBtn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
    }, [active.id, navState.level]);

    // Find the top-level bucket (movements with no category & no group)
    const topLevelEntry = hierarchy.find(c => c.isTopLevel);
    const topLevelItems = topLevelEntry?.groups[0]?.items ?? [];

    // Categorised entries only (excludes the top-level bucket)
    const categorisedEntries = hierarchy.filter(c => !c.isTopLevel);

    return (
        <div style={{ position: "fixed", bottom: "1.8rem", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", zIndex: 200, pointerEvents: "all", width: "100%" }}>
            {isMobile && showDesc && (
                <div style={{ maxWidth: "240px", textAlign: "center", marginBottom: "-3px", padding: "10px" }}>
                    <p style={{ margin: 0, fontSize: "13px", color: "rgba(255,255,255,0.8)", lineHeight: "1.6" }}>{active.description}</p>
                </div>
            )}

            <div style={{ marginBottom: "0.3rem", fontSize: "9px", letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap" }}>
                drag to rotate · {isMobile ? "pinch to zoom" : "scroll to zoom"}
            </div>

            <div style={{ textAlign: "center", width: "100%", padding: "0 30px", marginBottom: "12px" }}>
                <div style={{ display: "inline-block", maxWidth: "100%", position: "relative" }}>
                    <span style={{ fontFamily: "'Bitcount Grid Single', monospace", fontSize: "2.0rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.1, verticalAlign: "middle" }}>
                        {`${active.name}`}
                        {isMobile && (
                            <button onClick={() => setShowDesc(!showDesc)} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", background: "none", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50%", color: "rgba(255,255,255,0.4)", width: "22px", height: "22px", fontSize: "12px", marginLeft: "12px", verticalAlign: "middle", cursor: "pointer" }}>?</button>
                        )}
                    </span>
                </div>
            </div>

            {/* ── Category level ── */}
            {navState.level === "category" && (
                <div style={{ display: "flex", gap: "10px", alignItems: "center", maxWidth: "90vw" }}>
                    <div ref={scrollRef} style={{ display: "flex", overflowX: "auto", scrollbarWidth: "none", gap: "10px" }}>
                        {/* Categorised entries — drill into group or model */}
                        {categorisedEntries.map(cat => (
                            <button key={cat.name} style={btnStyle(false)} onClick={() => onNavState(cat.isFlat ? { level: "model", category: cat.name, group: cat.groups[0].name } : { level: "group", category: cat.name })}>
                                {cat.name}
                            </button>
                        ))}
                        {/* Top-level items rendered directly — no drill-down */}
                        {topLevelItems.map(m => (
                            <button key={m.id} data-active={active.id === m.id ? "true" : "false"} style={btnStyle(active.id === m.id)} onClick={() => onSelect(m)}>
                                {m.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Group level ── */}
            {navState.level === "group" && (
                <div style={{ display: "flex", gap: "10px", alignItems: "center", maxWidth: "90vw" }}>
                    <button style={btnStyle(false, true)} onClick={() => onNavState({ level: "category" })}>BACK</button>
                    <div ref={scrollRef} style={{ display: "flex", overflowX: "auto", scrollbarWidth: "none" }}>
                        {hierarchy.find(c => c.name === navState.category)?.groups.map(grp => (
                            <button key={grp.name} data-active={navState.group === grp.name ? "true" : "false"} style={btnStyle(navState.group === grp.name)} onClick={() => { onNavState({ level: "model", category: navState.category, group: grp.name }); onSelect(grp.items[0]); }}>{grp.name || "General"}</button>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Model level ── */}
            {navState.level === "model" && (
                <div style={{ display: "flex", gap: "8px", alignItems: "center", maxWidth: "90vw" }}>
                    <button style={btnStyle(false, true)} onClick={() => onNavState(hierarchy.find(c => c.name === navState.category)?.isFlat ? { level: "category" } : { level: "group", category: navState.category })}>BACK</button>
                    <div ref={scrollRef} style={{ display: "flex", overflowX: "auto", scrollbarWidth: "none" }}>
                        {hierarchy.find(c => c.name === navState.category)?.groups.find(g => g.name === navState.group)?.items.map(m => (
                            <button key={m.id} data-active={active.id === m.id ? "true" : "false"} style={btnStyle(active.id === m.id)} onClick={() => onSelect(m)}>{m.name}</button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── One-time camera initializer — sets position only on first mount ───────────
function CameraSetup({ position, fov }) {
    const { camera } = useThree();
    const initialized = useRef(false);
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;
        camera.position.set(...position);
        camera.fov = fov;
        camera.updateProjectionMatrix();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
}

// ── ROOT COMPONENT ───────────────────────────────────────────────────────────
export default function FiveOhSevenPortfolio() {
    const location = useLocation();
    const navigate = useNavigate();
    const hierarchy = useMemo(() => buildMovementHierarchy(MOVEMENTS), []);

    const [active, setActive] = useState(() => {
        // Pre-select movement from deep-link URL on first render
        const match = location.pathname.match(/\/507-mechanical-movements\/movement-(\d+)/);
        if (match) {
            const num = parseInt(match[1], 10);
            const found = MOVEMENTS.find(m => movementNumber(m) === num);
            if (found) return found;
        }
        return MOVEMENTS[0];
    });

    const [navState, setNavState] = useState(() => {
        // On a deep-link, pre-navigate the tab bar to the correct model
        const match = location.pathname.match(/\/507-mechanical-movements\/movement-(\d+)/);
        if (match) {
            const num = parseInt(match[1], 10);
            const found = MOVEMENTS.find(m => movementNumber(m) === num);
            if (found && found.category) {
                const grp = found.group ?? null;
                return {
                    level: "model",
                    category: found.category,
                    group: grp,
                };
            }
        }
        return { level: "category" };
    });
    const [showDesc, setShowDesc] = useState(false);
    const [paused, setPaused] = useState(false);
    const [resetSignal, setResetSignal] = useState(0);
    const [lights, setLights] = useState(DEFAULT_LIGHTS);
    const [modelDebug, setModelDebug] = useState(modelDebugDefaults(active));

    const showAbout = location.pathname.includes("/about");
    const showContact = location.pathname.includes("/contact");
    const isMovementDeepLink = /\/507-mechanical-movements\/movement-\d+/.test(location.pathname);

    const rotationAxis = DEBUG_LIGHTING ? modelDebug.rotationAxis : (active.rotationAxis ?? "y");

    const rotation = useMemo(() => {
        return DEBUG_LIGHTING
            ? [modelDebug.rotX, modelDebug.rotY, modelDebug.rotZ]
            : (active.rotation ?? [0, 0, 0]);
    }, [active.rotation, modelDebug.rotX, modelDebug.rotY, modelDebug.rotZ]);

    const initialView = useMemo(() => {
        return DEBUG_LIGHTING
            ? [modelDebug.viewX, modelDebug.viewY, modelDebug.viewZ]
            : (active.initialView ?? [0, 0, 10]);
    }, [active.initialView, modelDebug.viewX, modelDebug.viewY, modelDebug.viewZ]);

    const initialZoom = useMemo(() => {
        return DEBUG_LIGHTING ? modelDebug.initialZoom : (active.initialZoom ?? 100);
    }, [active.initialZoom, modelDebug.initialZoom]);

    // Resolve URL list — supports both legacy single `url` and new `urls` array
    const activeUrls = useMemo(() => {
        if (active.urls && active.urls.length > 0) return active.urls;
        if (active.url) return [active.url];
        return [];
    }, [active]);

    // ── Meta helpers ──────────────────────────────────────────────────────────
    const activeNum = movementNumber(active);
    const activeDesc = active.description ?? "";
    const metaDesc = activeDesc.length > 155 ? activeDesc.slice(0, 152) + "..." : activeDesc;
    const canonicalMovement = `https://www.ibrahimtayyab.com/507-mechanical-movements/movement-${activeNum}`;
    const movementTitle = `Movement ${activeNum}: ${active.name} — 507 Mechanical Movements`;

    const handleSetActive = (m) => {
        setActive(m);
        setModelDebug(modelDebugDefaults(m));
        setShowDesc(false);
        setPaused(false);
        navigate(`/507-mechanical-movements/movement-${movementNumber(m)}`, { replace: true });
    };

    const handleReset = () => {
        setModelDebug(modelDebugDefaults(active));
        setResetSignal(s => s + 1);
        setPaused(false);
    };

    return (
        <>
            {/* ── SEO meta ─────────────────────────────────────────────────── */}
            <Helmet>
                {isMovementDeepLink ? (
                    <title>{movementTitle}</title>
                ) : (
                    <title>507 Mechanical Movements — Interactive 3D — Ibrahim Tayyab</title>
                )}
                {isMovementDeepLink ? (
                    <meta name="description" content={metaDesc} />
                ) : (
                    <meta name="description" content="An interactive 3D interpretation of Henry T. Brown's 507 Mechanical Movements (1868). Explore animated mechanisms built by Ibrahim Tayyab." />
                )}
                {isMovementDeepLink && <link rel="canonical" href={canonicalMovement} />}
                {isMovementDeepLink && <meta property="og:title" content={movementTitle} />}
                {isMovementDeepLink && <meta property="og:description" content={metaDesc} />}
                {isMovementDeepLink && <meta property="og:url" content={canonicalMovement} />}
                {isMovementDeepLink && <meta property="og:type" content="article" />}
                {isMovementDeepLink && <meta property="og:site_name" content="507 Mechanical Movements by Ibrahim Tayyab" />}
                {isMovementDeepLink && <meta name="twitter:card" content="summary_large_image" />}
            </Helmet>

            <style>{`
                html, body, #root { height: 100%; width: 100%; margin: 0; padding: 0; overflow: hidden; }
                * { box-sizing: border-box; }
                ::-webkit-scrollbar { display: none; }
                @media (max-width: 640px) { .info-panel-507 { display: none !important; } }
            `}</style>

            <div style={{ width: "100vw", height: "100vh", background: "#080808", overflow: "hidden", position: "fixed", top: 0, left: 0, fontFamily: "'DM Sans', sans-serif" }}>

                {/* Canvas — top offset matches Portfolio.js */}
                <div style={{ position: "fixed", top: "-40px", left: 0, right: 0, bottom: 0 }}>
                    <Canvas style={{ width: "100%", height: "100%", touchAction: "none" }} gl={{ antialias: true }} frameloop="always">
                        <color attach="background" args={["#080808"]} />
                        <CameraSetup position={initialView} fov={40} />
                        <ambientLight intensity={lights.ambientIntensity} />
                        <directionalLight position={[lights.keyX, lights.keyY, lights.keyZ]} intensity={lights.keyIntensity} />
                        <directionalLight position={[lights.fillX, lights.fillY, lights.fillZ]} intensity={lights.fillIntensity} color="#ddeeff" />
                        <directionalLight position={[lights.rimX, lights.rimY, lights.rimZ]} intensity={lights.rimIntensity} color="#aabbcc" />
                        <Suspense fallback={null}>
                            <FiveOhSevenModel
                                key={active.id}
                                id={active.id}
                                urls={activeUrls}
                                paused={paused}
                                initialZoomPct={initialZoom}
                                rotation={rotation}
                                autoRotate={!paused}
                                rotationAxis={rotationAxis}
                                resetSignal={resetSignal}
                                metalness={lights.metalness}
                                roughness={lights.roughness}
                                ropeMetalness={lights.ropeMetalness}
                                ropeRoughness={lights.ropeRoughness}
                            />
                        </Suspense>
                    </Canvas>
                </div>

                {/* HUD overlay */}
                <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                    {!showAbout && !showContact && (
                        <>
                            <div className="info-panel-507" style={{ position: "absolute", top: "1.6rem", right: ".8rem", width: "220px", padding: "1rem", pointerEvents: "all" }}>
                                <p style={{ margin: 0, fontFamily: "'Bitcount Grid Single', monospace", fontSize: "1.75rem", color: "rgba(255,255,255,0.88)" }}>{active.name}</p>
                                <p style={{ margin: "10px 0 0", fontSize: "15px", color: "rgba(255,255,255,0.35)", lineHeight: 1.8 }}>{active.description}</p>
                            </div>
                            <MovementTabBar
                                active={active}
                                onSelect={handleSetActive}
                                navState={navState}
                                onNavState={setNavState}
                                hierarchy={hierarchy}
                                showDesc={showDesc}
                                setShowDesc={setShowDesc}
                            />
                        </>
                    )}

                    {/* About modal */}
                    {showAbout && (
                        <div onClick={() => navigate("/507-mechanical-movements")} style={{ position: "absolute", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(8,8,8,0.82)", backdropFilter: "blur(22px)", pointerEvents: "all" }}>
                            <div onClick={e => e.stopPropagation()} style={{ maxWidth: "520px", width: "90%", background: "rgba(12,12,12,0.96)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "2.5rem" }}>
                                <p style={{ margin: "0 0 0.4rem", fontFamily: "'Bitcount Grid Single', monospace", fontSize: "2rem", color: "rgba(255,255,255,0.88)" }}>About</p>
                                <p style={{ margin: "0 0 1.4rem", fontSize: "11px", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.22)" }}>Henry T. Brown, 1868 · 507 Mechanical Movements</p>

                                <p style={{ margin: "0 0 1rem", fontSize: "15px", color: "rgba(255,255,255,0.52)", lineHeight: 1.9 }}>
                                    <em>507 Mechanical Movements</em> was written by Henry T. Brown and published in 1868. Brown catalogued 507 mechanisms from gears, cams, levers, linkages, cranks to belts and more that formed the common building blocks of industrial machinery. The descriptions, diagrams, and mechanism numbering are entirely his work. Engineers have relied on his book for well over a century.
                                </p>
                                <p style={{ margin: "0 0 1rem", fontSize: "15px", color: "rgba(255,255,255,0.52)", lineHeight: 1.9 }}>
                                    This project's sole contribution is an interactive 3D interpretation. No intellectual credit for the movements themselves is claimed here. 3D models built by Ibrahim Tayyab. The goal is to work through all 507.
                                </p>

                                <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "1.2rem", display: "flex", gap: "1rem", alignItems: "center" }}>
                                    <button onClick={() => navigate("/507-mechanical-movements")} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "rgba(255,255,255,0.4)", fontSize: "11px", padding: "8px 20px", cursor: "pointer", letterSpacing: "0.1em", fontFamily: "'DM Sans', sans-serif" }}>← Back</button>
                                    <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.18)", letterSpacing: "0.1em", fontFamily: "'DM Sans', sans-serif" }}>{MOVEMENTS.length} of 507 modelled</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Contact modal */}
                    {showContact && (
                        <div onClick={() => navigate("/507-mechanical-movements")} style={{ position: "absolute", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(8,8,8,0.82)", backdropFilter: "blur(22px)", pointerEvents: "all" }}>
                            <div onClick={e => e.stopPropagation()} style={{ maxWidth: "400px", width: "90%", background: "rgba(12,12,12,0.96)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "2.5rem" }}>
                                <p style={{ margin: "0 0 1.6rem", fontFamily: "'Bitcount Grid Single', monospace", fontSize: "2rem", color: "rgba(255,255,255,0.88)" }}>Contact</p>
                                <a href="mailto:hi@ibrahimtayyab.com" style={{ color: "rgba(255,255,255,0.5)", textDecoration: "none", fontSize: "16px" }}>hi@ibrahimtayyab.com</a>
                                <br />
                                <a href="https://www.linkedin.com/in/ibrahimtayyab" target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.5)", textDecoration: "none", fontSize: "16px", marginTop: "0.8rem", display: "inline-block" }}>linkedin.com/in/ibrahimtayyab</a>
                                <button onClick={() => navigate("/507-mechanical-movements")} style={{ marginTop: "2rem", display: "block", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "rgba(255,255,255,0.4)", fontSize: "11px", padding: "8px 20px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>← Back</button>
                            </div>
                        </div>
                    )}
                </div>

                {DEBUG_LIGHTING && (
                    <LightDebugPanel
                        lights={lights}
                        onChange={setLights}
                        paused={paused}
                        onTogglePause={() => setPaused(p => !p)}
                        onReset={handleReset}
                        modelDebug={modelDebug}
                        onModelDebugChange={setModelDebug}
                        activeMovement={active}
                    />
                )}
            </div>
        </>
    );
}